/**
 * @file RentalMachineDetailPage.jsx
 * @description Rental-focused machine detail — opened from the Rentals page
 *              (NOT the device-management telemetry view). "Command Center"
 *              layout: an identity hero with an OEM reference render, a cluster
 *              of real machine stats, a to-scale Working Envelope drawn from the
 *              data sheet, a lifecycle timeline built from real dates, operators
 *              (CRUD), the last rental + last service, and the full OEM spec.
 *
 *              Every value binds to real data from
 *              GET /rentals/machine/:equipmentId/overview. Panels that would
 *              need history we don't collect (utilisation, hour trends) are
 *              omitted rather than faked — the reach diagram, timeline and data
 *              sheet carry the visual weight instead.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Chip, Button, CircularProgress, Alert,
  Breadcrumbs, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, Tooltip, Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Place as PlaceIcon,
  MyLocation as LocateIcon,
  SignalWifiOff as NoSignalIcon,
  AccessTimeFilled as HoursIcon,
  Event as YearIcon,
  Height as HeightIcon,
  FitnessCenter as LoadIcon,
  LocalShipping as RentalIcon,
  Build as ServiceIcon,
  Straighten as ReachIcon,
  Scale as WeightIcon,
  Terrain as GradeIcon,
  SwapHoriz as ChangeStatusIcon,
  Person as OperatorIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  History as TimelineIcon,
  Description as DataSheetIcon,
  TrendingUp as TrendIcon,
  Groups as OccupantsIcon,
  SwapVert as UpOverIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import rentalService from '../../services/rentalService';
import { statusLabel, statusColor } from '../../utils/statusLabels';
import { canChangeMachineStatus } from '../../utils/permissions';
import { useAuth } from '../../context/AuthContext';
import ChangeStatusDialog from './ChangeStatusDialog';

// --- accents (data-viz palette, aligned with theme.js semantic colours) -------
const A = {
  teal: '#0f766e', blue: '#2563eb', indigo: '#4f46e5', purple: '#7c3aed',
  amber: '#d97706', cyan: '#0891b2', slate: '#475569', green: '#16a34a', red: '#dc2626',
  sky: '#0ea5e9', violet: '#818cf8',
};

// --- formatting helpers -------------------------------------------------------
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};
const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
};
const fmtNum = (v, frac = 1) => (v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: frac }));
const daysBetween = (from, to) => {
  if (!from) return null;
  const a = new Date(from), b = to ? new Date(to) : new Date();
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
  return Math.max(0, Math.round((b - a) / 86400000)) + 1; // inclusive of both endpoints
};

// spec-sheet label + unit derivation
const SPEC_LABELS = {
  overall_length_transported_m: 'Overall length (transported)',
  overall_width_transported_m: 'Overall width (transported)',
  overall_height_transported_m: 'Overall height (transported)',
  rated_load_std_kg: 'Rated load (standard)',
  rated_load_opt_kg: 'Rated load (optional)',
  max_occupants: 'Max occupants',
  max_platform_height_m: 'Max platform height',
  max_working_height_m: 'Max working height',
  max_platform_radius_m: 'Max platform radius',
  max_working_radius_m: 'Max working radius',
  max_boom_up_angle_deg: 'Max boom up angle',
  max_boom_down_angle_deg: 'Max boom down angle',
  max_up_and_over_height_m: 'Up-and-over height',
  max_depth_of_descend_m: 'Max depth of descent',
  gross_machine_weight_kg: 'Gross machine weight',
  tail_swing_m: 'Tail swing',
  tire_contact_pressure_kpa: 'Tyre contact pressure',
  grade_ability_pct: 'Gradeability',
  max_slope_descent_deg: 'Max slope descent',
  max_slope_ascent_deg: 'Max slope ascent',
  max_side_slope_deg: 'Max side slope',
};
const UNIT_SUFFIX = [
  ['_mps', 'm/s'], ['_kpa', 'kPa'], ['_rpm', 'rpm'], ['_deg', '°'], ['_pct', '%'],
  ['_kg', 'kg'], ['_nm', 'N·m'], ['_bar', 'bar'], ['_ah', 'Ah'], ['_db', 'dB'],
  ['_m', 'm'], ['_l', 'L'], ['_n', 'N'], ['_s', 's'],
];
const prettyKey = (k) => (SPEC_LABELS[k] || k.replace(/_(m|kg|deg|pct|kpa|l|mps|db|rpm|nm|bar|ah|n|s)$/, '').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()));
const unitOf = (k) => { for (const [suf, u] of UNIT_SUFFIX) if (k.endsWith(suf)) return u; return ''; };
const fmtSpecVal = (k, v) => {
  if (v == null || v === '') return '—';
  const u = unitOf(k);
  if (Array.isArray(v)) return `${v.join('–')}${u ? ' ' + u : ''}`;
  return `${v}${u ? ' ' + u : ''}`;
};
const specNum = (v) => {
  const n = Array.isArray(v) ? Number(v[v.length - 1]) : Number(v);
  return Number.isFinite(n) ? n : null;
};

// --- small presentational bits ------------------------------------------------

// A pill used for the identity chips under the model name.
const IdChip = ({ icon, label, value, mono }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.9, px: 1.25, py: 0.75, borderRadius: 2.5, bgcolor: '#f8fafc', boxShadow: 'inset 0 0 0 1px rgba(148,163,184,0.28)' }}>
    {icon}
    {label && <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.03em' }}>{label}</Typography>}
    <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', fontFamily: mono ? 'monospace' : undefined }}>{value}</Typography>
  </Box>
);

// Tiny inline SVG visualisations for the stat tiles (all fed by REAL data).
const Sparkline = ({ points, accent }) => {
  if (!points || points.length < 2) return null;
  const w = 84, h = 28, pad = 3;
  const hs = points.map((p) => p.h);
  const min = Math.min(...hs), span = (Math.max(...hs) - min) || 1;
  const xy = (p, i) => [pad + (i * (w - 2 * pad)) / (points.length - 1), h - pad - ((p.h - min) / span) * (h - 2 * pad)];
  const pts = points.map((p, i) => xy(p, i).map((n) => Math.round(n * 10) / 10).join(',')).join(' ');
  const [lx, ly] = xy(points[points.length - 1], points.length - 1);
  return (
    <Box component="svg" viewBox={`0 0 ${w} ${h}`} sx={{ width: w, height: h, display: 'block', flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={accent} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" opacity="0.85" />
      <circle cx={lx} cy={ly} r="2.6" fill={accent} />
    </Box>
  );
};
const Donut = ({ pct, accent }) => {
  const r = 15, c = 2 * Math.PI * r, p = Math.max(0, Math.min(100, pct));
  return (
    <Box component="svg" viewBox="0 0 40 40" sx={{ width: 30, height: 30, flexShrink: 0 }}>
      <circle cx="20" cy="20" r={r} fill="none" stroke={`${accent}22`} strokeWidth="5" />
      <circle cx="20" cy="20" r={r} fill="none" stroke={accent} strokeWidth="5" strokeLinecap="round"
        strokeDasharray={`${(c * p) / 100} ${c}`} transform="rotate(-90 20 20)" />
    </Box>
  );
};
const MiniBars = ({ items, accent }) => {
  if (!items || !items.length) return null;
  const max = Math.max(...items.map((i) => i.days)) || 1;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: 28, flexShrink: 0 }}>
      {items.slice(0, 5).map((it, i) => (
        <Tooltip key={i} title={`${it.contract_id} · ${it.days} day${it.days === 1 ? '' : 's'}`}>
          <Box sx={{ width: 9, borderRadius: '3px 3px 1px 1px', height: `${Math.max(14, (it.days / max) * 100)}%`, bgcolor: accent, opacity: 0.35 + 0.65 * (it.days / max) }} />
        </Tooltip>
      ))}
    </Box>
  );
};

// Big-number stat tile for the cluster (all values are real machine facts).
// The header is a fixed height so the big numbers share one baseline across the
// row whether or not a tile carries a mini-viz; the viz hides on tiny screens.
const StatTile = ({ icon, label, value, unit, sub, accent, viz }) => (
  <Paper elevation={0} sx={{ p: 2.25, borderRadius: 4, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, minHeight: 30 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 2, bgcolor: `${accent}15`, color: accent, flexShrink: 0 }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary' }} noWrap>{label}</Typography>
      {viz && <Box sx={{ ml: 'auto', display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>{viz}</Box>}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, color: 'text.primary' }}>{value}</Typography>
      {unit && <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>{unit}</Typography>}
    </Box>
    {sub && <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 'auto', pt: 1, lineHeight: 1.4 }} noWrap>{sub}</Typography>}
  </Paper>
);

// Section card with a tinted, titled header. `bodySx` lets a caller centre or
// otherwise lay out the body so a stretched card fills without dead space.
const SectionCard = ({ title, icon, accent = A.slate, tint, right, children, sx, bodySx }) => (
  <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, borderBottom: '1px solid', borderColor: 'divider', background: tint || `${accent}0d` }}>
      <Box sx={{ color: accent, display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
      {right && <Box sx={{ ml: 'auto' }}>{right}</Box>}
    </Box>
    <Box sx={{ p: 2.5, flex: 1, ...bodySx }}>{children}</Box>
  </Paper>
);

/* Marker fill per lifecycle status. statusColor() returns MUI palette NAMES
   ('primary', 'warning', …) which mean nothing inside raw marker CSS, so the
   map keeps its own hex map aligned with the same statuses. */
const MARKER_COLOR = {
  RENTAL: A.blue, DE_HIRED: A.purple, SERVICE: A.amber, BREAKDOWN: A.red,
  AVAILABLE: A.green, WAITING_FOR_RENT: A.cyan, MAINTENANCE: A.amber,
  TESTING: A.slate, SALE: A.slate,
};
const markerColor = (status) => MARKER_COLOR[String(status || '').toUpperCase().replace(/\s+/g, '_')] || A.teal;

/* Last known position. The marker is a divIcon (same trick the contract live map
   uses) so we never depend on Leaflet's bundled marker PNGs, which CRA's asset
   pipeline rewrites. `key` on the map forces a re-centre when the fix changes. */
const LocationMap = ({ lat, lng, label, status, accent }) => (
  <MapContainer
    key={`${lat},${lng}`}
    center={[lat, lng]}
    zoom={15}
    scrollWheelZoom={false}
    style={{ height: '100%', width: '100%' }}
  >
    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
    <Marker
      position={[lat, lng]}
      icon={L.divIcon({
        className: 'rental-machine-marker',
        html: `<div style="background:${accent};width:16px;height:16px;border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 2px ${accent}55,0 2px 8px rgba(0,0,0,.35)"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      })}
    >
      <Popup>
        <Box sx={{ minWidth: 150 }}>
          <Typography variant="subtitle2" fontWeight={800}>{label}</Typography>
          {status && <Typography variant="caption" color="text.secondary">{status}</Typography>}
        </Box>
      </Popup>
    </Marker>
  </MapContainer>
);

const KVRow = ({ label, value, last }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderBottom: last ? 'none' : '1px solid', borderColor: 'rgba(148,163,184,0.14)' }}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', color: 'text.primary' }}>{value || '—'}</Typography>
  </Box>
);

// --- to-scale Working Envelope (drawn from the real data sheet) ----------------
const r1 = (n) => Math.round(n * 10) / 10;
const ReachDiagram = ({ spec }) => {
  const H = specNum(spec.max_working_height_m);
  const R = specNum(spec.max_working_radius_m);
  const U = specNum(spec.max_up_and_over_height_m);
  if (!H) return null;

  const W = 560, groundY = 320, originX = 120, topPad = 40, rightPad = 150;
  const availY = groundY - topPad;
  const availX = (W - rightPad) - originX;
  const scale = Math.min(availY / H, availX / (R || H * 0.55)); // equal x/y scale = to scale
  const heightY = r1(groundY - H * scale);
  const radiusX = r1(originX + (R || 0) * scale);
  const upoverY = U ? r1(groundY - U * scale) : null;
  const apexX = r1(originX + (radiusX - originX) * 0.52);
  const grids = [5, 10, 15, 20, 25].filter((g) => g < H - 0.4).map((g) => ({ g, y: r1(groundY - g * scale) }));

  // illustrative reach blob, bounded by the exact height & radius measures
  const envelope = `M ${originX},${groundY} L ${originX},${r1(heightY + (groundY - heightY) * 0.12)} `
    + `Q ${originX},${heightY} ${apexX},${heightY} `
    + `Q ${radiusX},${heightY} ${radiusX},${r1((heightY + groundY) / 2)} `
    + `L ${radiusX},${groundY} Z`;

  return (
    <Box>
      <Box component="svg" viewBox={`0 0 ${W} 372`} sx={{ width: '100%', display: 'block' }}>
        <defs>
          <linearGradient id="env-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={A.teal} stopOpacity="0.16" />
            <stop offset="1" stopColor={A.teal} stopOpacity="0.03" />
          </linearGradient>
        </defs>

        {/* height gridlines + labels */}
        <g stroke="#e8edf3" strokeWidth="1" strokeDasharray="3 4">
          {grids.map((gr) => <line key={gr.g} x1={originX} y1={gr.y} x2={radiusX + 40} y2={gr.y} />)}
        </g>
        <g fontFamily="monospace" fontSize="10" fill="#94a3b8">
          {grids.map((gr) => <text key={gr.g} x={originX - 34} y={gr.y + 3}>{gr.g} m</text>)}
        </g>

        {/* reach envelope */}
        <path d={envelope} fill="url(#env-fill)" stroke={A.teal} strokeWidth="1.4" strokeOpacity="0.4" strokeDasharray="5 5" />

        {/* ground */}
        <line x1={originX - 58} y1={groundY} x2={radiusX + 60} y2={groundY} stroke="#0f172a" strokeWidth="2" />
        <g stroke="#cbd5e1" strokeWidth="1.4">
          <line x1={originX - 46} y1={groundY} x2={originX - 52} y2={groundY + 8} />
          <line x1={originX - 26} y1={groundY} x2={originX - 32} y2={groundY + 8} />
          <line x1={originX - 6} y1={groundY} x2={originX - 12} y2={groundY + 8} />
        </g>

        {/* up-and-over arc */}
        {upoverY != null && (
          <>
            <path d={`M ${originX},${groundY - 20} Q ${originX - 34},${r1((groundY - 20 + upoverY) / 2)} ${originX},${upoverY}`} fill="none" stroke={A.amber} strokeWidth="1.4" strokeDasharray="4 4" />
            <text x={originX - 92} y={r1(upoverY) + 4} fontFamily="monospace" fontSize="9.5" fill="#b45309">{r1(U)} m up-over</text>
          </>
        )}

        {/* machine glyph */}
        <g>
          <rect x={originX - 22} y={groundY - 16} width="56" height="15" rx="3" fill="#334155" />
          <circle cx={originX - 8} cy={groundY} r="7" fill="#1e293b" />
          <circle cx={originX + 22} cy={groundY} r="7" fill="#1e293b" />
          <rect x={originX + 8} y={groundY - 34} width="22" height="20" rx="3" fill="#475569" />
          <path d={`M ${originX + 19},${groundY - 30} L ${r1((originX + apexX) / 2)},${r1((groundY - 30 + heightY) / 2)} L ${apexX},${r1(heightY + 8)}`} fill="none" stroke={A.teal} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
          <rect x={apexX - 12} y={heightY - 4} width="24" height="16" rx="2.5" fill="none" stroke={A.teal} strokeWidth="3" />
        </g>

        {/* max working height measure */}
        <line x1={apexX} y1={groundY} x2={apexX} y2={heightY} stroke={A.blue} strokeWidth="1.3" strokeDasharray="5 4" />
        <circle cx={apexX} cy={heightY} r="3" fill={A.blue} />
        <g transform={`translate(${radiusX + 8},${heightY})`}>
          <rect x="0" y="-4" width="128" height="34" rx="7" fill="#eff6ff" />
          <text x="10" y="12" fontFamily="'Outfit', sans-serif" fontSize="13" fontWeight="800" fill="#1d4ed8">{r1(H)} m</text>
          <text x="10" y="25" fontFamily="Inter, sans-serif" fontSize="9.5" fill="#3b6fd4">max working height</text>
        </g>

        {/* max working radius measure */}
        {R != null && (
          <>
            <line x1={originX} y1={groundY + 16} x2={radiusX} y2={groundY + 16} stroke={A.purple} strokeWidth="1.3" />
            <line x1={originX} y1={groundY + 12} x2={originX} y2={groundY + 20} stroke={A.purple} strokeWidth="1.3" />
            <line x1={radiusX} y1={groundY + 12} x2={radiusX} y2={groundY + 20} stroke={A.purple} strokeWidth="1.3" />
            <text x={r1((originX + radiusX) / 2 - 46)} y={groundY + 34} fontFamily="'Outfit', sans-serif" fontSize="12" fontWeight="700" fill="#6d28d9">{r1(R)} m working radius</text>
          </>
        )}
      </Box>

      {/* legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
        <LegendDot color={A.teal} text={`Working height ${r1(H)} m`} />
        {R != null && <LegendDot color={A.purple} text={`Working radius ${r1(R)} m`} />}
        {U != null && <LegendDot color={A.amber} text={`Up-and-over ${r1(U)} m`} />}
      </Box>
    </Box>
  );
};
const LegendDot = ({ color, text }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: color }} />
    <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary' }}>{text}</Typography>
  </Box>
);

// --- lifecycle timeline (built only from real, dated events) -------------------
const Timeline = ({ events }) => (
  <Box sx={{ position: 'relative' }}>
    {/* rail, tucked behind the markers */}
    <Box sx={{ position: 'absolute', left: 11, top: 10, bottom: 10, width: '2px', bgcolor: '#f1f5f9' }} />
    {events.map((ev, i) => (
      <Box key={i} sx={{ position: 'relative', pl: 4.5, mb: i === events.length - 1 ? 0 : 2.75 }}>
        {/* ringed marker: tinted disc + white cutout + solid dot */}
        <Box sx={{
          position: 'absolute', left: 0, top: 1, width: 24, height: 24, borderRadius: '50%',
          bgcolor: `${ev.color}1f`, border: '4px solid #fff', zIndex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ev.color }} />
        </Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled' }}>{ev.when}</Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary', lineHeight: 1.35 }}>{ev.title}</Typography>
        {ev.detail && (
          <Typography sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1.4 }}>{ev.detail}</Typography>
        )}
      </Box>
    ))}
  </Box>
);

// --- hero action buttons ------------------------------------------------------
const HeroBtn = ({ onClick, href, icon, children, variant = 'ghost' }) => {
  const base = { display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderRadius: 2.5, fontSize: 13.5, fontWeight: 600, textDecoration: 'none', cursor: 'pointer', border: 'none', lineHeight: 1.2, transition: 'all .18s ease', fontFamily: 'inherit' };
  const styles = {
    primary: { ...base, color: '#fff', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 8px 20px -6px rgba(37,99,235,.55)', '&:hover': { filter: 'brightness(1.08)' } },
    secondary: { ...base, color: 'text.primary', bgcolor: '#fff', boxShadow: 'inset 0 0 0 1px rgba(148,163,184,0.35)', '&:hover': { bgcolor: '#f1f5f9' } },
    ghost: { ...base, color: 'text.secondary', bgcolor: 'transparent', '&:hover': { color: 'text.primary', bgcolor: 'rgba(148,163,184,0.12)' } },
  };
  const Comp = href ? 'a' : 'button';
  return (
    <Box component={Comp} onClick={onClick} href={href} target={href ? '_blank' : undefined} rel={href ? 'noopener' : undefined} sx={styles[variant]}>
      {icon}{children}
    </Box>
  );
};

export default function RentalMachineDetailPage() {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditStatus = canChangeMachineStatus(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const dataSheetRef = useRef(null);

  // operators
  const [operators, setOperators] = useState([]);
  const [opForm, setOpForm] = useState({ operator_name: '', shift: '', from_date: '' });
  const [opBusy, setOpBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await rentalService.getMachineOverview(equipmentId);
      setData(res);
      setOperators(res?.operators || []);
    } catch (e) {
      setError(e.message || 'Failed to load machine details.');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => { load(); }, [load]);

  const reloadOperators = useCallback(async () => {
    try { const r = await rentalService.getOperators(equipmentId); setOperators(r?.operators || []); }
    catch (e) { /* keep current */ }
  }, [equipmentId]);

  const addOperator = async () => {
    if (!opForm.operator_name.trim()) return;
    setOpBusy(true);
    try {
      await rentalService.addOperator(equipmentId, {
        operator_name: opForm.operator_name.trim(),
        shift: opForm.shift.trim() || null,
        from_date: opForm.from_date || null,
      });
      setOpForm({ operator_name: '', shift: '', from_date: '' });
      await reloadOperators();
    } catch (e) { setError(e.message || 'Failed to add operator.'); }
    finally { setOpBusy(false); }
  };
  const completeOperator = async (op) => {
    const today = new Date().toISOString().slice(0, 10);
    try { await rentalService.updateOperator(op.id, { to_date: today }); await reloadOperators(); }
    catch (e) { setError(e.message || 'Failed to complete operator.'); }
  };
  const editFromDate = async (op, from_date) => {
    try { await rentalService.updateOperator(op.id, { from_date }); await reloadOperators(); }
    catch (e) { setError(e.message || 'Failed to update date.'); }
  };
  const removeOperator = async (op) => {
    try { await rentalService.deleteOperator(op.id); await reloadOperators(); }
    catch (e) { setError(e.message || 'Failed to delete operator.'); }
  };

  const goBack = () => navigate('/rentals');
  const scrollToSheet = () => dataSheetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  }
  if (error) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mb: 2 }}>Back to Rentals</Button>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>
      </Box>
    );
  }

  const m = data?.machine || {};
  const rental = data?.last_rental || null;
  const service = data?.last_service || null;
  const specs = data?.specs || null;
  const spec = specs?.model || null;

  const title = [m.brand, m.model].filter(Boolean).join(' ') || m.equipment_id || 'Machine';
  const mapUrl = m.location ? `https://www.google.com/maps?q=${m.location.lat},${m.location.lng}` : null;
  const locationLabel = m.location ? (m.location.place || `${m.location.lat.toFixed(4)}, ${m.location.lng.toFixed(4)}`) : null;
  const maxHeight = spec ? specNum(spec.max_working_height_m) : null;
  const maxRadius = spec ? specNum(spec.max_working_radius_m) : null;
  const thisYear = new Date().getFullYear();
  const ageYears = m.year_of_manufacture ? thisYear - Number(m.year_of_manufacture) : null;
  const lastHireDays = rental ? daysBetween(rental.from, rental.to) : null;

  // headline data-sheet tiles (the "how high it reaches" set)
  const tiles = spec ? [
    { icon: <HeightIcon fontSize="small" />, label: 'MAX WORKING HEIGHT', value: fmtSpecVal('max_working_height_m', spec.max_working_height_m), accent: A.teal, tint: '#f0fdfa', ring: '#ccfbf1' },
    { icon: <HeightIcon fontSize="small" />, label: 'MAX PLATFORM HEIGHT', value: fmtSpecVal('max_platform_height_m', spec.max_platform_height_m), accent: A.blue, tint: '#eff6ff', ring: '#dbeafe' },
    { icon: <ReachIcon fontSize="small" />, label: 'MAX WORKING RADIUS', value: fmtSpecVal('max_working_radius_m', spec.max_working_radius_m), accent: A.purple, tint: '#f5f3ff', ring: '#ede9fe' },
    { icon: <LoadIcon fontSize="small" />, label: 'RATED LOAD', value: fmtSpecVal('rated_load_std_kg', spec.rated_load_std_kg), accent: A.amber, tint: '#fffbeb', ring: '#fef3c7' },
    { icon: <UpOverIcon fontSize="small" />, label: 'UP-AND-OVER HEIGHT', value: fmtSpecVal('max_up_and_over_height_m', spec.max_up_and_over_height_m), accent: A.cyan, tint: '#ecfeff', ring: '#cffafe' },
    { icon: <WeightIcon fontSize="small" />, label: 'GROSS WEIGHT', value: fmtSpecVal('gross_machine_weight_kg', spec.gross_machine_weight_kg), accent: A.slate, tint: '#f8fafc', ring: '#e2e8f0' },
    { icon: <GradeIcon fontSize="small" />, label: 'GRADEABILITY', value: fmtSpecVal('grade_ability_pct', spec.grade_ability_pct), accent: A.green, tint: '#f0fdf4', ring: '#dcfce7' },
    { icon: <OccupantsIcon fontSize="small" />, label: 'MAX OCCUPANTS', value: fmtSpecVal('max_occupants', spec.max_occupants), accent: A.red, tint: '#fef2f2', ring: '#fee2e2' },
  ].filter((t) => t.value !== '—') : [];

  // stat cluster — four real machine facts (per the command-center mockup)
  const st = data?.stats || {};
  const hire = st.hire || null;
  const utilPct = hire ? Math.round((hire.last60_days / (hire.window_days || 60)) * 100) : null;
  const nextSvc = st.next_service || null;

  const stats = [
    {
      icon: <HoursIcon fontSize="small" />, accent: A.teal, label: 'ENGINE HOURS',
      value: m.engine_hours != null ? fmtNum(m.engine_hours) : '—', unit: m.engine_hours != null ? 'h' : '',
      sub: st.hours_month_delta != null
        ? `${st.hours_month_delta >= 0 ? '+' : ''}${fmtNum(st.hours_month_delta)} h this month`
        : (m.last_communication ? `as of ${fmtDate(m.last_communication)}` : 'No telemetry yet'),
      viz: <Sparkline points={st.hours_trend} accent={A.teal} />,
    },
    utilPct != null
      ? {
        icon: <TrendIcon fontSize="small" />, accent: A.blue, label: 'UTILIZATION · 60d',
        value: String(utilPct), unit: '%',
        sub: `On hire ${hire.last60_days} of ${hire.window_days || 60} days`,
        viz: <Donut pct={utilPct} accent={A.blue} />,
      }
      : {
        icon: <HeightIcon fontSize="small" />, accent: A.blue, label: 'MAX WORKING HEIGHT',
        value: maxHeight != null ? fmtNum(maxHeight, 2) : '—', unit: maxHeight != null ? 'm' : '',
        sub: maxRadius != null ? `working radius ${fmtNum(maxRadius, 2)} m` : 'per OEM data sheet',
      },
    nextSvc
      ? {
        icon: <ServiceIcon fontSize="small" />, accent: A.amber, label: 'NEXT SERVICE',
        value: fmtNum(nextSvc.hours_until), unit: 'h',
        sub: `Service ${nextSvc.checklist} due at ${fmtNum(nextSvc.next_at, 0)} h`,
      }
      : {
        icon: <YearIcon fontSize="small" />, accent: A.indigo, label: 'YEAR OF MFG.',
        value: m.year_of_manufacture || '—', unit: '',
        sub: ageYears != null ? `~${ageYears} yr${ageYears === 1 ? '' : 's'} in service` : 'not on record',
      },
    hire
      ? {
        icon: <RentalIcon fontSize="small" />, accent: A.purple, label: 'ON HIRE DAYS',
        value: String(hire.total_days), unit: hire.total_days === 1 ? 'day' : 'days',
        sub: `${hire.contracts_count} contract${hire.contracts_count === 1 ? '' : 's'} to date`,
        viz: <MiniBars items={hire.per_contract} accent={A.purple} />,
      }
      : {
        icon: <RentalIcon fontSize="small" />, accent: A.purple, label: 'LAST HIRE',
        value: lastHireDays != null ? String(lastHireDays) : '—', unit: lastHireDays != null ? (lastHireDays === 1 ? 'day' : 'days') : '',
        sub: rental?.customer || 'No rentals on record',
      },
  ];

  // timeline — only real, dated events, newest first. Actual status transitions
  // (asset_status_logs, via the API) are the backbone; rental/service-derived
  // events fill in only when no matching transition was logged.
  const logs = data?.timeline || [];
  const STATUS_EVENT = {
    RENTAL: { color: A.blue, title: (l) => `On Rent${l.contract_id ? ` — ${l.contract_id}` : ''}` },
    DE_HIRED: { color: A.violet, title: (l) => `De-hired${l.contract_id ? ` from ${l.contract_id}` : ''}` },
    SERVICE: { color: A.amber, title: () => 'Service opened' },
    BREAKDOWN: { color: A.red, title: () => 'Breakdown reported' },
    AVAILABLE: { color: A.green, title: () => 'Ready for rent' },
    WAITING_FOR_RENT: { color: A.cyan, title: () => 'Waiting for rent' },
  };
  const events = logs.map((l) => {
    const meta = STATUS_EVENT[l.to_status] || { color: A.slate, title: () => statusLabel(l.to_status) };
    const o = (l.details && l.details.opening) || {};
    const detail = [
      o.location || o.dehireReason || o.description || null,
      l.ticket_id ? `ticket #${l.ticket_id}` : null,
      l.by ? `by ${l.by}` : null,
    ].filter(Boolean).join(' · ') || null;
    return { ts: new Date(l.ts).getTime(), when: fmtDate(l.ts), color: meta.color, title: meta.title(l), detail };
  });
  if (m.last_communication) events.push({ ts: new Date(m.last_communication).getTime(), when: fmtDateTime(m.last_communication) + ' IST', color: A.sky, title: 'Telemetry received', detail: [m.engine_hours != null ? `${fmtNum(m.engine_hours)} h` : null, m.location?.place].filter(Boolean).join(' · ') || null });
  const hasLog = (s) => logs.some((l) => l.to_status === s);
  if (rental?.to && !hasLog('DE_HIRED')) events.push({ ts: new Date(rental.to).getTime(), when: fmtDate(rental.to), color: A.violet, title: `De-hired from ${rental.customer || 'customer'}`, detail: rental.contract_id || null });
  if (rental?.from && !hasLog('RENTAL')) events.push({ ts: new Date(rental.from).getTime(), when: fmtDate(rental.from), color: A.blue, title: `On Rent — ${rental.customer || 'customer'}`, detail: [rental.contract_id, rental.site].filter(Boolean).join(' · ') || null });
  if (service?.date && !hasLog('SERVICE') && !hasLog('BREAKDOWN')) events.push({ ts: new Date(service.date).getTime(), when: fmtDate(service.date), color: A.amber, title: `${(service.type || 'Service').replace(/_/g, ' ')}${service.status ? ' — ' + service.status.replace(/_/g, ' ') : ''}`, detail: service.notes || null });
  if (m.tcu_installed_at) events.push({ ts: new Date(m.tcu_installed_at).getTime(), when: fmtDate(m.tcu_installed_at), color: A.slate, title: 'Commissioned into fleet', detail: m.device_id ? `TCU ${m.device_id} installed` : null });
  events.sort((a, b) => b.ts - a.ts);

  const statusChip = m.status
    ? <Chip size="small" label={statusLabel(m.status)} color={statusColor(m.status)} sx={{ fontWeight: 700 }} />
    : <Chip size="small" label="No status" variant="outlined" sx={{ fontWeight: 700 }} />;

  const hasEnvelope = spec && maxHeight != null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* breadcrumbs + back */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Breadcrumbs>
          <Link component="button" underline="hover" color="inherit" onClick={goBack}>Rentals</Link>
          <Typography color="text.secondary">Machine</Typography>
          <Typography color="text.primary" fontWeight={700} sx={{ fontFamily: 'monospace' }}>{m.serial_number || m.equipment_id}</Typography>
        </Breadcrumbs>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack} size="small">Back to Rentals</Button>
      </Box>

      {/* ============================ HERO ============================ */}
      <Paper elevation={0} sx={{ position: 'relative', borderRadius: 5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(720px 420px at 86% -25%, rgba(13,148,136,.07), transparent 60%), radial-gradient(620px 440px at 4% 125%, rgba(37,99,235,.05), transparent 62%)', pointerEvents: 'none' }} />
        <Grid container sx={{ position: 'relative' }}>
          {/* hero left */}
          <Grid size={{ xs: 12, md: 7 }} sx={{ p: { xs: 2.5, md: 4 }, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2, flexWrap: 'wrap' }}>
              {statusChip}
              {m.last_communication && (
                <Chip size="small" variant="outlined"
                  icon={<DotIcon sx={{ fontSize: '10px !important', color: `${A.green} !important`, animation: 'pulse 2s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } } }} />}
                  label={`Last data ${fmtDateTime(m.last_communication)} IST`}
                  sx={{ fontWeight: 500, color: 'text.secondary', bgcolor: '#f8fafc' }} />
              )}
            </Box>

            {m.category && (
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', color: A.teal, mb: 0.75, textTransform: 'uppercase' }}>
                {m.category}
              </Typography>
            )}
            <Typography variant="h2" sx={{ fontSize: { xs: 30, md: 42 }, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.04 }}>{title}</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              <IdChip label="SERIAL" value={m.serial_number || '—'} mono />
              {locationLabel && <IdChip icon={<PlaceIcon sx={{ fontSize: 15, color: A.teal }} />} value={locationLabel} />}
            </Box>

            <Box sx={{ flex: 1, minHeight: 16 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 3 }}>
              {canEditStatus
                ? <HeroBtn variant="primary" onClick={() => setStatusOpen(true)} icon={<ChangeStatusIcon sx={{ fontSize: 17 }} />}>
                    {m.status === 'DE_HIRED' ? 'Re-hire machine' : m.status === 'RENTAL' ? 'De-hire machine' : 'Change status'}
                  </HeroBtn>
                : (mapUrl && <HeroBtn variant="primary" href={mapUrl} icon={<LocateIcon sx={{ fontSize: 17 }} />}>Locate on map</HeroBtn>)}
              {canEditStatus && m.status !== 'SERVICE' && m.status !== 'BREAKDOWN' && (
                <HeroBtn variant="secondary" onClick={() => setStatusOpen(true)} icon={<ServiceIcon sx={{ fontSize: 17, color: A.amber }} />}>Log service</HeroBtn>
              )}
              {canEditStatus && mapUrl && <HeroBtn variant="secondary" href={mapUrl} icon={<LocateIcon sx={{ fontSize: 17, color: A.teal }} />}>Locate</HeroBtn>}
            </Box>
            {spec && (
              <Box sx={{ mt: 1.5 }}>
                <HeroBtn variant="ghost" onClick={scrollToSheet} icon={<DataSheetIcon sx={{ fontSize: 17 }} />}>Data sheet</HeroBtn>
              </Box>
            )}
          </Grid>

          {/* hero right — live location map, full width (no machine image) */}
          <Grid size={{ xs: 12, md: 5 }} sx={{ p: 2, display: 'flex' }}>
            {/* explicit minHeight: Leaflet needs a definite height to render */}
            <Box sx={{ flex: 1, position: 'relative', borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', minHeight: { xs: 260, md: 300 } }}>
              {m.location ? (
                <LocationMap
                  lat={m.location.lat}
                  lng={m.location.lng}
                  label={m.serial_number || m.equipment_id}
                  status={`${statusLabel(m.status)}${locationLabel ? ` · ${locationLabel}` : ''}`}
                  accent={markerColor(m.status)}
                />
              ) : (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', color: 'text.disabled', gap: 0.5, p: 2, textAlign: 'center' }}>
                  <NoSignalIcon sx={{ fontSize: 24 }} />
                  <Typography variant="body2" fontWeight={700}>No GPS fix</Typography>
                </Box>
              )}

              {maxHeight != null && (
                <Box sx={{ position: 'absolute', right: 16, bottom: 14, zIndex: 1000, display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.6, borderRadius: 2, bgcolor: '#0f172a', color: '#e2e8f0', fontSize: 11, fontWeight: 600 }}>
                  <Box component="span" sx={{ color: '#5eead4' }}>{fmtNum(maxHeight, 2)} m</Box> max working height
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ======================== STAT CLUSTER ======================== */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {stats.map((s, i) => (
          <Grid size={{ xs: 6, md: 3 }} key={i}><StatTile {...s} /></Grid>
        ))}
      </Grid>

      {/* ========== WORKING ENVELOPE · LAST RENTAL · LIFECYCLE TIMELINE ========== */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {hasEnvelope && (
          <Grid size={{ xs: 12, md: 4 }}>
            <SectionCard title="Working Envelope" icon={<ReachIcon />} accent={A.teal}
              right={<Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', fontFamily: 'monospace' }}>TO SCALE · {specs?.source_model || m.model}</Typography>}
              bodySx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <ReachDiagram spec={spec} />
            </SectionCard>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: hasEnvelope ? 4 : 6 }}>
          <SectionCard title="Last Rental" icon={<RentalIcon />} accent={A.blue} tint="linear-gradient(90deg,#eff6ff,#ffffff)"
            right={rental?.status ? <Chip size="small" label={statusLabel(rental.status)} sx={{ fontWeight: 700, bgcolor: '#e2e8f0', color: 'text.secondary' }} /> : null}
            bodySx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {rental ? (
              <>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <Avatar sx={{ width: 42, height: 42, borderRadius: 2.5, background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)', fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: 18 }}>
                      {(rental.customer || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: 18, lineHeight: 1.1, minWidth: 0 }} noWrap>{rental.customer || '—'}</Typography>
                  </Box>
                  <KVRow label="Contract" value={<Box component="span" sx={{ fontFamily: 'monospace' }}>{rental.contract_id || '—'}</Box>} />
                  <KVRow label="Site" value={rental.site} last />
                </Box>

                {/* rental period progress — real dates, elapsed % of Start→End */}
                {rental.from && rental.to && (() => {
                  const fromMs = new Date(rental.from).getTime();
                  const toMs = new Date(rental.to).getTime();
                  if (isNaN(fromMs) || isNaN(toMs) || toMs <= fromMs) return null;
                  const pct = Math.max(0, Math.min(100, Math.round(((Date.now() - fromMs) / (toMs - fromMs)) * 100)));
                  return (
                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ height: 6, width: '100%', borderRadius: 3, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: 'linear-gradient(90deg,#1d4ed8,#3b82f6)', transition: 'width .3s ease' }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Rental Start</Typography>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Project End</Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">No rentals on record for this machine.</Typography>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: hasEnvelope ? 4 : 6 }}>
          <SectionCard title="Lifecycle Timeline" icon={<TimelineIcon />} accent={A.slate}
            bodySx={{ maxHeight: { md: 360 }, overflowY: 'auto' }}>
            {events.length > 0
              ? <Timeline events={events} />
              : <Typography variant="body2" color="text.secondary">No dated lifecycle events yet.</Typography>}
          </SectionCard>
        </Grid>
      </Grid>

      {/* ======================== OPERATORS ========================== */}
      {(m.status === 'RENTAL' || operators.length > 0) && (
        <SectionCard title="Operators" icon={<OperatorIcon />} accent={A.blue}
          right={<Chip size="small" label={operators.filter((o) => o.active).length} sx={{ bgcolor: A.blue, color: '#fff', fontWeight: 700, height: 22 }} />}
          sx={{ mb: 2.5 }}>
          {operators.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: canEditStatus ? 2 : 0 }}>No operators added yet.</Typography>
          ) : (
            <TableContainer sx={{ mb: canEditStatus ? 2 : 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Operator</TableCell>
                    <TableCell>Shift</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell align="center">Status</TableCell>
                    {canEditStatus && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operators.map((op) => (
                    <TableRow key={op.id} hover sx={op.active ? undefined : { opacity: 0.6 }}>
                      <TableCell sx={{ fontWeight: 700 }}>{op.operator_name}</TableCell>
                      <TableCell>{op.shift || '—'}</TableCell>
                      <TableCell>
                        {canEditStatus ? (
                          <TextField type="date" size="small" variant="standard" value={op.from_date || ''}
                            onChange={(e) => editFromDate(op, e.target.value)}
                            sx={{ '& input': { fontSize: 13, py: 0.25 } }} />
                        ) : fmtDate(op.from_date)}
                      </TableCell>
                      <TableCell>{op.to_date ? fmtDate(op.to_date) : <Typography variant="caption" color="text.disabled">ongoing</Typography>}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={op.active ? 'Active' : 'Completed'} color={op.active ? 'primary' : 'default'} sx={{ height: 20, fontWeight: 700 }} />
                      </TableCell>
                      {canEditStatus && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {op.active && (
                            <Tooltip title="Complete (set end date to today)">
                              <IconButton size="small" color="success" onClick={() => completeOperator(op)} aria-label="Complete operator"><CheckIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => removeOperator(op)} aria-label="Delete operator"><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {canEditStatus && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <TextField label="Operator name" size="small" value={opForm.operator_name}
                onChange={(e) => setOpForm((f) => ({ ...f, operator_name: e.target.value }))} sx={{ minWidth: 180 }} />
              <TextField label="Shift" size="small" placeholder="Day 08:00-18:00" value={opForm.shift}
                onChange={(e) => setOpForm((f) => ({ ...f, shift: e.target.value }))} sx={{ minWidth: 160 }} />
              <TextField label="From date" type="date" size="small" InputLabelProps={{ shrink: true }} value={opForm.from_date}
                onChange={(e) => setOpForm((f) => ({ ...f, from_date: e.target.value }))} />
              <Button variant="contained" startIcon={<AddIcon />} onClick={addOperator}
                disabled={opBusy || !opForm.operator_name.trim()} sx={{ fontWeight: 700 }}>Add operator</Button>
            </Box>
          )}
        </SectionCard>
      )}

      {/* ======================== DATA SHEET ======================== */}
      <Box ref={dataSheetRef}>
        <SectionCard title="Machine Data Sheet" icon={<DataSheetIcon />} accent={A.teal}
          right={specs && !specs.exact ? (
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Reference: {specs.source_model} series
            </Typography>
          ) : null}>
          {!specs ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Data sheet not on file for <b>{m.model || 'this model'}</b>.
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Published specs are currently available for the Dingli BA/BT 16–22 CRT2 boom-lift series.
              </Typography>
            </Box>
          ) : (
            <>
              {/* headline tiles — the 6 "how high it reaches" measures as pills */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                {tiles.slice(0, 6).map((t, i) => (
                  <Box key={i} sx={{
                    flex: '1 1 150px', minWidth: 150, borderRadius: '999px', px: 3, py: 1.75,
                    bgcolor: t.tint, boxShadow: `inset 0 0 0 1px ${t.ring}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.4,
                  }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.02em', color: t.accent, textTransform: 'uppercase' }}>{t.label}</Typography>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: 20, fontWeight: 800, color: 'text.primary' }}>{t.value}</Typography>
                  </Box>
                ))}
              </Box>

              {/* second-tier measures (gradeability, occupants) — distinct box shape */}
              {tiles.length > 6 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                  {tiles.slice(6).map((t, i) => (
                    <Box key={i} sx={{
                      borderRadius: 4, px: 3, py: 1.5, bgcolor: t.tint, boxShadow: `inset 0 0 0 1px ${t.ring}`,
                      display: 'inline-flex', flexDirection: 'column', gap: 0.4,
                    }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.02em', color: t.accent, textTransform: 'uppercase' }}>{t.label}</Typography>
                      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: 20, fontWeight: 800, color: 'text.primary' }}>{t.value}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* full spec — up to four columns */}
              <Grid container columnSpacing={6} rowSpacing={1}>
                {Object.entries(spec).map(([k, v]) => (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} key={k}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, py: 1, borderBottom: '1px solid', borderColor: 'rgba(148,163,184,0.14)' }}>
                      <Typography variant="caption" color="text.secondary">{prettyKey(k)}</Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmtSpecVal(k, v)}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {specs.source_model && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
                  Source: OEM training manual, {specs.source_model}{specs.exact ? '' : ` (same series as ${m.model || ''})`}.
                </Typography>
              )}
            </>
          )}
        </SectionCard>
      </Box>

      {/* Change-status (reuses the app-wide dialog). */}
      <ChangeStatusDialog
        open={statusOpen}
        device={{ equipment_id: m.equipment_id, device_id: m.device_id, serial_number: m.serial_number }}
        currentStatus={m.status}
        onClose={() => setStatusOpen(false)}
        onSuccess={() => { setStatusOpen(false); load(); }}
      />
    </Box>
  );
}
